"use strict";

var _ = require("lodash");
var uuid = require("node-uuid");

module.exports = /* @ngInject */ function(
  $q,
  defaultsRepository,
  workspaceService,
  openFinApi,
  RediWindowRepository,
  RediWindowFactory,
  OpenfinWindowFactory,
  cookieService,
  sideBySideApi,
  RediWindowInstanceInitializer
) {
  var that = this;

  const DEFAULT_FILTER = {
    filterId: "layoutAndOptions",
    filterValue: "default"
  };

  that.createRediWindowFromMenuItem = function(menuItem, appInitParams, state) {
    return isEikonContainedMessenger(menuItem).then(function(
      isEikonContainedMessenger
    ) {
      if (isEikonContainedMessenger) {
        return maybeShowMessenger(state);
      }
      return getOrCreateHiddenRediWindowFromMenuItem(
        menuItem,
        appInitParams,
        state
      ).then(function(rediWindow) {
        return rediWindow.show();
      });
    });
  };

  that.createRediWindowFromWorkspace = function(
    menuItem,
    workspace,
    windowName
  ) {
    return isEikonContainedMessenger(menuItem).then(function(
      isEikonContainedMessenger
    ) {
      if (!isEikonContainedMessenger) {
        return getOrCreateHiddenRediWindowFromWorkspace(
          menuItem,
          null,
          workspace,
          windowName
        ).then(function(rediWindow) {
          return rediWindow.show();
        });
      }
    });
  };

  function isEikonContainedMessenger(menuItem) {
    return RediWindowFactory.createFromMenuItem(
      menuItem
    ).isEikonContainedMessenger();
  }

  function maybeShowMessenger(state) {
    if (state !== "minimized") {
      return sideBySideApi.showMessenger();
    }
    return $q.when();
  }

  function getOrCreateHiddenRediWindowFromMenuItem(
    menuItem,
    appInitParams,
    state
  ) {
    if (canOpenMore(menuItem)) {
      return workspaceService
        .getOrCreateWorkspace()
        .then(function(workspace) {
          return createHiddenRediWindowFromWorkspace(
            menuItem,
            appInitParams,
            workspace
          );
        })
        .then(function(rediWindow) {
          return rediWindow.setState(state).then(function() {
            return rediWindow;
          });
        });
    } else {
      var existingRediWindow = getExistingRediWindows(menuItem)[0];
      return existingRediWindow.restoreIfMinimized().then(function() {
        return existingRediWindow;
      });
    }
  }

  function getOrCreateHiddenRediWindowFromWorkspace(
    menuItem,
    appInitParams,
    workspace,
    windowName
  ) {
    if (canOpenMore(menuItem)) {
      return createHiddenRediWindowFromWorkspace(
        menuItem,
        appInitParams,
        workspace,
        windowName
      );
    } else {
      var existingRediWindow = getExistingRediWindows(menuItem)[0];
      return applyDefaultOrWorkspaceLayout(existingRediWindow, workspace);
    }
  }

  function createHiddenRediWindowFromWorkspace(
    menuItem,
    appInitParams,
    workspace,
    windowName
  ) {
    return createRediWindow(menuItem, appInitParams, windowName).then(function(
      rediWindow
    ) {
      return applyDefaultOrWorkspaceLayout(rediWindow, workspace);
    });
  }

  function canOpenMore(menuItem) {
    return (
      getExistingRediWindows(menuItem).length < menuItem.maxNumberOfWindows
    );
  }

  function getExistingRediWindows(menuItem) {
    return RediWindowRepository.getWindowsByType(menuItem.type);
  }

  function createRediWindow(menuItem, appInitParams, windowName) {
    var rediWindow = RediWindowFactory.createFromMenuItem(menuItem);
    return rediWindow.isEikonContainer().then(function(isEikonContainer) {
      if (isEikonContainer) {
        var eikonWindowInitParams = appInitParams || menuItem.defaultParams;
        if (rediWindow.menuItem.eikonAppId) {
          return sideBySideApi
            .launchEikonWindow(
              rediWindow.menuItem.eikonAppId,
              eikonWindowInitParams
            )
            .then(function() {
              return rediWindow;
            });
        } else if (rediWindow.menuItem.url) {
          return sideBySideApi
            .launchEikonWindowByUrl(rediWindow.menuItem.url)
            .then(function() {
              return rediWindow;
            });
        } else {
          return $q.reject(
            new Error("eikonAppId nor url found on " + menuItem.type)
          );
        }
      }
      return prepareOpenFinRediWindow(rediWindow, windowName)
        .then(function(rediWindow) {
          return OpenfinWindowFactory.createOpenfinWindow(
            rediWindow,
            appInitParams
          );
        })
        .then(function(windowInstance) {
          return RediWindowInstanceInitializer.setWindowInstanceAndInitialize(
            rediWindow,
            windowInstance
          );
        });
    });
  }

  function generateWindowName(rediWindow) {
    /***
     * For always-open windows, we need to use a single name to be workspace-safe.
     *
     * Take the following scenario:
     *
     * 1. Save workspace
     *   - Always-open window is saved with its UUID name.
     * 2. Restart REDIPlus
     *   - Always-open window is open with new UUID name.
     * 3. Load the workspace
     *   - Always-open window is still open with the new UUID name, which is different from the one in the workspace.
     *
     * Now because the actual window name is different from what's in workspace, if linking is saved in the workspace, it breaks.
     *
     * This doesn't happen for regular windows because they are closed and re-opened with workspace window name when workspace is loaded.
     ***
     * For Singleton window(Max Number of windows is 1), we need to use a single workspace name to be able to restore workspace from localStorage.
     *
     * Take the following scenario:
     *
     * 1. Open the Singleton Window
     *    - Singleton Window is opened with its UUID name
     * 2. Close the Singleton Window without saving workspace
     * 3. Re-open the Singleton Window without using workspace
     *    - The same Singleton Window is opened with a new UUID name and now there are 2 workspace config for this Singleton Window with different UUID name
     *
     * On step 3, the window should restore the temporarily stored workspace from local storage.
     *
     * For that, we need the window in step 1 and step 3 to have the same workspace name.
     */
    return rediWindow.isAlwaysOpen() || rediWindow.onlyAllowsOpeningOneWindow()
      ? rediWindow.getType()
      : uuid.v4();
  }

  function prepareOpenFinRediWindow(rediWindow, windowName) {
    setWindowName(windowName, rediWindow);
    return setCookiesIfNecessary(rediWindow);
  }

  function setCookiesIfNecessary(rediWindow) {
    return rediWindow.isEikonContainer().then(function(isEikonContainer) {
      if (rediWindow.isEikonWindow() && !isEikonContainer) {
        return cookieService.setEikonCookies(rediWindow);
      } else if (rediWindow.isGoldmanWindow()) {
        return cookieService.setGoldmanCookies(rediWindow);
      }
      return rediWindow;
    });
  }

  function setWindowName(windowName, rediWindow) {
    if (windowName) {
      rediWindow.setName(windowName);
    } else {
      rediWindow.setName(generateWindowName(rediWindow));
    }
  }

  function getDefaultLayoutOrUndefined(defaultsConfig) {
    if (
      defaultsConfig &&
      defaultsConfig[0] &&
      defaultsConfig[0].defaults &&
      defaultsConfig[0].defaults.layout
    ) {
      return defaultsConfig[0].defaults.layout;
    }
  }

  function applyDefaultOrWorkspaceLayout(rediWindow, workspace) {
    return rediWindow.isOpenfinContainer().then(function(isOpenFinContainer) {
      if (isOpenFinContainer) {
        var workspaceLayout = getWorkspaceLayout(workspace, rediWindow);
        return defaultsRepository
          .get(rediWindow.menuItem.type, DEFAULT_FILTER)
          .then(defaultsConfig => {
            const defaultLayout = getDefaultLayoutOrUndefined(defaultsConfig);
            return rediWindow
              .setLayout(defaultLayout || workspaceLayout)
              .then(function() {
                return rediWindow;
              });
          });
      } else {
        return rediWindow;
      }
    });
  }

  function getWorkspaceLayout(workspace, rediWindow) {
    var workspaceWindow = workspace.getWindow(
      rediWindow.getType(),
      rediWindow.getName()
    );
    var layout = workspaceWindow && workspaceWindow.getLayout();
    if (layout && layout.width && layout.height) {
      return layout;
    } else {
      return getDefaultLayout(rediWindow);
    }
  }

  function getDefaultLayout(rediWindow) {
    return _.assign(
      {},
      workspaceService.getDefaultLayout(),
      rediWindow.getDefaultWindowSize() || {}
    );
  }
};



// WEBPACK FOOTER //
// ./app/infrastructure/RediWindowService.js